import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { formatDate } from '../../utils/dateUtils';
import { useDeleteReminder } from '../../hooks/useReminders';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const DeleteReminderModal = ({ isOpen, onClose, reminder }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const deleteReminder = useDeleteReminder();
    const { t } = useLanguage();

    const handleDelete = async () => {
        setDeleteError('');
        setIsDeleting(true);

        try {
            await deleteReminder.mutateAsync({ reminderUid: reminder.uid });
            onClose();
        } catch (error) {
            console.error('Error deleting reminder:', error);
            setDeleteError(error.message || t(KEYS.REMINDER_DELETE_ERROR));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) {
            setDeleteError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.REMINDER_DELETE_TITLE)}
            subtitle={t(KEYS.COMMON_CANNOT_UNDO)}
            size="small"
            footer={
                <>
                    <Button variant="outline" onClick={handleClose} disabled={isDeleting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? t(KEYS.COMMON_DELETING) : t(KEYS.COMMON_DELETE)}
                    </Button>
                </>
            }
        >
            {deleteError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {deleteError}
                </div>
            )}

            <div className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-5 h-5 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-text-primary font-medium">
                        Delete reminder <strong>{reminder?.title}</strong>?
                    </p>
                </div>

                {reminder && (
                    <div className="bg-surface-overlay rounded border border-surface-border p-4 space-y-2">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">{t(KEYS.COMMON_DATE_DUE)}</span>
                            <span className="text-sm font-mono text-text-primary">{formatDate(reminder.due_date)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-text-muted">{t(KEYS.COMMON_FREQUENCY)}</span>
                            <span className="text-sm text-text-primary capitalize">{reminder.frequency}</span>
                        </div>
                    </div>
                )}

                <p className="text-sm text-text-muted">
                    This reminder will be permanently removed from the system.
                </p>
            </div>
        </Modal>
    );
};

export default DeleteReminderModal;
